/**
 * O gancho que registra o service worker — e a guarda que o impede de tentar em desenvolvimento.
 *
 * ## O defeito que isto conserta
 *
 * `main.tsx` chamava `registrarServiceWorker()` sem condicao nenhuma. Em `vite dev` **nao existe
 * `/sw.js`**: o `vite.config.ts` marca `devOptions: { enabled: SW_EM_DEV }` com `SW_EM_DEV`
 * falso, entao o worker so nasce no `vite build`. O servidor de dev respondia o `index.html`
 * para `/sw.js`, o navegador recusava registrar um script de tipo `text/html`, e cada carga
 * imprimia **dois erros de console**. O app degradava bem — `registro.ts` ja captura a falha e
 * segue sem SW —, mas console sujo em dev e onde o erro de verdade se esconde.
 *
 * ## A decisao: o SW nao registra em dev, e o motivo nao e um `if (DEV)`
 *
 * A pergunta certa nao e "estamos em desenvolvimento?", e sim **"este build produziu um
 * `/sw.js`?"**. Sao coisas diferentes, e escrever a primeira e como as duas divergem: no dia em
 * que alguem ligar `devOptions.enabled` para depurar o precache, um `if (import.meta.env.DEV)`
 * continuaria bloqueando o registro do worker que o build agora gera, e a pessoa perderia a
 * tarde procurando por que o SW nao sobe.
 *
 * Por isso a fonte da verdade e **uma constante so**, `SW_EM_DEV` no `vite.config.ts`, que
 * alimenta ao mesmo tempo o plugin (`devOptions.enabled`) e o `define` `__SW_DISPONIVEL__` que
 * esta funcao le. Ligar o SW em dev e trocar `false` por `true` num lugar; nao ha combinacao
 * meio-aplicada possivel.
 *
 * ## O que isto NAO muda
 *
 * Nada do `PWA §4.6` (kill switch) nem do `§5.2` (atualizacao por toque do usuario). Aqueles
 * sao propriedades do worker **publicado** e do `updateViaCache: 'none'` em `registro.ts`, que
 * segue intacto: em qualquer build de producao `__SW_DISPONIVEL__` e `true` e o caminho e
 * exatamente o de antes. A guarda so decide se ha o que registrar.
 */

import { useEffect, useState } from 'react';

import { registrarServiceWorker, type RegistroDoSw } from './registro.js';

/**
 * Injetado por `define` no `vite.config.ts`. `true` em todo build de producao; em dev, igual a
 * `devOptions.enabled` do `vite-plugin-pwa`.
 */
declare const __SW_DISPONIVEL__: boolean;

/**
 * Este build produz `/sw.js`?
 *
 * O `typeof` cobre o runtime que nao passa pelo `define` — Vitest, e o mesmo padrao que
 * `app/ambiente.ts` usa para `__BUILD_ID__`. O default e `false`, que e a leitura fail-closed:
 * na duvida, nao registra.
 */
export function swDisponivelNesteBuild(): boolean {
  return typeof __SW_DISPONIVEL__ === 'boolean' ? __SW_DISPONIVEL__ : false;
}

export interface EstadoDoServiceWorker {
  /** Ha um worker em `waiting`. A UI mostra o convite de atualizar (`PWA §5.2`). */
  readonly temVersaoNova: boolean;
  /** Aplica a versao em espera. Chamado **so** por toque do usuario (PWA-010). */
  readonly aplicarAtualizacao: () => void;
}

export interface OpcoesDoGancho {
  /** Injetavel para o teste; em runtime vem de `swDisponivelNesteBuild()`. */
  readonly disponivel?: boolean;
  /** Injetavel para o teste; em runtime e o registrador de verdade. */
  readonly registrar?: typeof registrarServiceWorker;
}

export function useRegistroDoSw(opcoes: OpcoesDoGancho = {}): EstadoDoServiceWorker {
  const disponivel = opcoes.disponivel ?? swDisponivelNesteBuild();
  const registrar = opcoes.registrar ?? registrarServiceWorker;

  const [registro, setRegistro] = useState<RegistroDoSw | null>(null);
  const [temVersaoNova, setTemVersaoNova] = useState(false);

  useEffect(() => {
    // A GUARDA. Sem ela, `vite dev` tenta registrar um `/sw.js` que o servidor responde como
    // HTML, e sao 2 erros de console por carga.
    if (!disponivel) return undefined;

    // O registro fica num efeito, e nao no topo do modulo, por um motivo medido: registrar o SW
    // durante a avaliacao do bundle compete por CPU com a primeira renderizacao, e no
    // aparelho-alvo (`PWA §0.1`, Snapdragon 4xx com throttling 4x) isso aparece no LCP.
    const registrado = registrar({
      aoDetectarVersaoNova: () => {
        setTemVersaoNova(true);
      },
    });
    setRegistro(registrado);
    return () => {
      registrado?.desmontar();
    };
  }, [disponivel, registrar]);

  return {
    temVersaoNova,
    aplicarAtualizacao: () => {
      registro?.aplicar();
    },
  };
}
